import './ApplianceSingle.scss';

import AvailabilityComponent from '../Primitives/AvailabilityComponent';
import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import GatsbyImageWrapper from '../GatsbyImageWrapper';
import { IAppliance } from '../../interfaces';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import React from 'react';
import empty_star from '@progymedia/sbi-web-englander/src/images/icons/Empty_Star_Light.svg';
import full_star from '@progymedia/sbi-web-englander/src/images/icons/Full_Star_Light.svg';
import half_star from '@progymedia/sbi-web-englander/src/images/englander-icons/Star-Half-Icon.svg';
import popFrontURL from '../../utils/popFrontURL';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import { useTranslation } from 'react-i18next';

export default function ApplianceSingle(props: {
    appliance: IAppliance;
    id?: string;
    onTouchStart?: (e: any) => void;
    onTouchEnd?: (e: any) => void;
    isPromo?: boolean;
    isDiscontinued?: boolean;
}) {
    const { appliance, id, onTouchStart, onTouchEnd, isPromo, isDiscontinued } =
        props;
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const [price] = useProductPrice(appliance?.code);
    const { t } = useTranslation('pages');

    const productReviews = useProductReviews(appliance?.code);

    const goToSinglePage = () => {
        if (!isDiscontinued) {
            navigateTranslated(
                popFrontURL(appliance?.slug?.[currentLocale], 2)
            );
        } else {
            navigateTranslated(`/archives/${appliance?.code?.toLowerCase()}/`);
        }
    };

    return (
        <div
            className='appliance-single'
            id={id || undefined}
            onTouchStart={(e) => onTouchStart && onTouchStart(e)}
            onTouchEnd={(e) => onTouchEnd && onTouchEnd(e)}
        >
            {isPromo && (
                <div className='promo-tag'>
                    <div className='content'>Promo</div>
                </div>
            )}
            <div className='single-image' onClick={() => goToSinglePage()}>
                <GatsbyImageWrapper
                    className='product-img-wrapper'
                    fields={appliance?.fields}
                />
            </div>
            <div className='single-content'>
                <span className='single-sku'>{appliance?.code}</span>
                <span className='single-title'>
                    {appliance?.title?.[currentLocale]}
                </span>
                <div className='single-ratings'>
                    {!isDiscontinued &&
                        productReviews &&
                        productReviews?.count > 0 && (
                            <div className='rating'>
                                {RenderStars()}{' '}
                                <span className='rating-count-container'>
                                    <span className='rating-count'>
                                        {productReviews.count}
                                    </span>
                                </span>
                            </div>
                        )}
                </div>
                {!isDiscontinued && (
                    <AvailabilityComponent product={appliance} />
                )}
                <div className='price-and-btn'>
                    {!isDiscontinued && RenderProductPrice()}
                    <Button
                        className='see-product'
                        label={t('home.see-product')}
                        onClick={() => goToSinglePage()}
                    />
                </div>
            </div>
        </div>
    );

    function RenderStars() {
        const averageRating = productReviews?.average ?? 0;
        const stars = [];
        let hasRating = false;

        for (let i = 1; i < 6; i++) {
            if (i <= averageRating) {
                hasRating = true;
                stars.push(
                    <img width={20} key={`star_${i}`} src={full_star} alt='' />
                );
            } else {
                const currentFloor = Math.floor(averageRating);
                const projectedFloor = Math.floor(averageRating - 0.5);
                if (projectedFloor === currentFloor && i === currentFloor + 1) {
                    stars.push(
                        <img
                            width={20}
                            key={`star_${i}`}
                            src={half_star}
                            alt=''
                        />
                    );
                } else {
                    stars.push(
                        <img
                            width={20}
                            key={`star_${i}`}
                            src={empty_star}
                            alt=''
                        />
                    );
                }
            }
        }

        if (hasRating) return stars.map((star) => star);
    }

    function RenderProductPrice() {
        if (price?.regularPrice.value === price?.finalPrice.value) {
            return (
                <span className='single-price regular'>
                    <span className='normal-price'>
                        {LocalePrice(currentLocale, price?.regularPrice.value)}
                    </span>
                </span>
            );
        } else if (
            price?.regularPrice.value &&
            price?.finalPrice.value &&
            price.regularPrice.value !== price.finalPrice.value
        ) {
            return (
                <span className='single-price'>
                    <span className='regular-price'>
                        {LocalePrice(currentLocale, price?.regularPrice.value)}
                    </span>
                    <span className='final-price'>
                        {LocalePrice(currentLocale, price?.finalPrice.value)}
                    </span>
                </span>
            );
        }
    }
}
